import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Divider,
  Group,
  Popover,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core"
import { IconCirclePlus, IconFilter, IconX } from "@tabler/icons-react"
import _, { isArray, noop } from "lodash"
import { Dispatch, ReactNode, SetStateAction, useMemo, useState } from "react"

import {
  Button,
  MultiSelect,
  SearchInput,
  Select,
  AsyncSelect,
  ActionIcon,
  AsyncMultiSelect,
} from "@tfg/ui"

import { DateFilter } from "./date-filter/DateFilter"
import {
  DateFilterDto,
  DateFilterOperator,
} from "./date-filter/date-filter.types"

export interface FilterOptions<T> {
  label: string
  key: keyof T
  options: {
    label: string
    value: T[keyof T]
  }[]
  customRender?: (
    item: ComboboxLikeRenderOptionInput<ComboboxItem>
  ) => ReactNode
  disableSort?: boolean
  type:
    | "select"
    | "multiSelect"
    | "dateRange"
    | "async-select"
    | "async-multi-select"
  asyncRefetch?: (searchQuery?: string) => Promise<any>
  isAsyncLoading?: boolean
}

export interface FilterBuilderProps<T> {
  title?: string
  searchPlaceholder?: string
  options: FilterOptions<T>[]
  filters: {
    [key in keyof T]: T[key]
  }
  setFilters: Dispatch<
    SetStateAction<{
      [key in keyof T]: T[key]
    }>
  >
  locale?: string
  labelTranslationResolver: Record<DateFilterOperator, string>
  valueResolver: (filter: DateFilterDto) => string
}

export const FilterBuilder = <T extends Partial<Record<string, any>>>({
  filters,
  setFilters,
  options,
  title = "Filters",
  searchPlaceholder,
  locale,
  labelTranslationResolver,
  valueResolver,
}: FilterBuilderProps<T>) => {
  const [search, setSearch] = useState<string>("")
  const theme = useMantineTheme()
  const getLabel = (key: keyof T) => {
    return options.find((option) => option.key === key)?.label ?? key.toString()
  }

  const getOptions = (key: keyof T) => {
    return options
      .find((option) => option.key === key)
      ?.options.map((option) => ({
        ...option,
        label: option.label,
        value: option.value,
      }))
  }

  const sortOptions = (key: keyof T) => {
    return options.find((option) => option.key === key)?.disableSort
      ? false
      : undefined
  }

  const getOptionLabel = (key: keyof T, value: T[keyof T]) => {
    const filterType =
      options.find((option) => option.key === key)?.type ?? "select"

    if (filterType === "dateRange") {
      if (!value) return null
      return valueResolver(value as DateFilterDto)
    }

    const optionConfig = options.find((option) => option.key === key)
    if (!optionConfig) return null

    const isBooleanValue =
      typeof value === "boolean" ||
      value === "true" ||
      value === "false" ||
      value === true ||
      value === false

    if (isBooleanValue) {
      const boolValue =
        typeof value === "boolean" ? value : value === "true" || value === true
      const label = optionConfig.options.find((option) => {
        const optionValue =
          typeof option.value === "boolean"
            ? option.value
            : option.value === "true" || option.value === true
        return optionValue === boolValue
      })?.label
      return label ?? null
    }

    const label = optionConfig.options.find(
      (option) => option.value === value
    )?.label
    if (!label) return null

    return label
  }

  const getType = (key: keyof T) => {
    return options.find((option) => option.key === key)?.type ?? "select"
  }

  const filteredOptions = useMemo(() => {
    return _.chain(options)
      .filter(
        (option) =>
          option.label.toLowerCase().includes(search.toLowerCase()) &&
          !Object.keys(filters).includes(option.key.toString())
      )
      .orderBy("label")
      .value()
  }, [search, options, filters])

  const filterLength = useMemo(() => {
    return Object.keys(filters).length
  }, [filters])

  const handleDateRangeChange = (
    key: keyof T,
    dateFilter: DateFilterDto | null
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: dateFilter,
    }))
  }

  const label = getFilterButtonLabel(filters, getLabel, getOptionLabel, title)

  return (
    <Popover position="bottom-start">
      <Popover.Target>
        <Button
          variant={filterLength > 0 ? "secondary" : "ghost"}
          size="xs"
          px="xs"
          rightSection={
            <IconFilter
              stroke={theme.other.icon.stroke}
              size={theme.other.icon.size}
            />
          }
          fw={500}
        >
          {label}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <SearchInput
            placeholder={searchPlaceholder}
            inputValue={search}
            delay={0}
            onOpen={noop}
            onChange={(value) => {
              setSearch(value?.toString() ?? "")
            }}
          />
          {Object.entries(filters).map(([key, value]) => (
            <Group key={key} justify="space-between">
              <Text>{getLabel(key as keyof T)}</Text>
              <Group wrap="nowrap" gap={1}>
                <Group wrap="nowrap" w="100%">
                  {getType(key) === "select" ? (
                    <Select
                      searchable
                      value={
                        value === undefined || value === null
                          ? null
                          : typeof value === "boolean"
                            ? String(value)
                            : (value ?? null)
                      }
                      customRender={
                        options.find((option) => option.key === key)
                          ?.customRender
                      }
                      data={getOptions(key)?.map((option) => ({
                        ...option,
                        value:
                          typeof option.value === "boolean"
                            ? String(option.value)
                            : option.value,
                      }))}
                      onChange={(val) => {
                        const option = options.find((opt) => opt.key === key)
                        const isBooleanFilter = option?.options.some(
                          (opt) => typeof opt.value === "boolean"
                        )

                        if (val === null) {
                          setFilters({
                            ...filters,
                            [key]: undefined,
                          })
                        } else if (isBooleanFilter) {
                          // Convert string to boolean
                          const boolValue = val === "true"
                          setFilters({
                            ...filters,
                            [key]: boolValue as T[keyof T],
                          })
                        } else {
                          setFilters({
                            ...filters,
                            [key]: val as T[keyof T],
                          })
                        }
                      }}
                      comboboxProps={{
                        withinPortal: false,
                      }}
                      sort={sortOptions(key)}
                    />
                  ) : getType(key) === "multiSelect" ? (
                    <MultiSelect
                      searchable
                      value={isArray(value) ? value : value ? [value] : []}
                      data={getOptions(key)}
                    />
                  ) : getType(key) === "dateRange" ? (
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                    >
                      <DateFilter
                        labelTranslationResolver={labelTranslationResolver}
                        value={value}
                        locale={locale ?? "fr"}
                        onChange={(filter) =>
                          handleDateRangeChange(key as keyof T, filter)
                        }
                        key={key}
                      />
                    </div>
                  ) : getType(key) === "async-select" ? (
                    <AsyncSelect
                      value={value ? value.toString() : null}
                      options={getOptions(key)}
                      asyncRefetch={
                        options.find((option) => option.key === key)
                          ?.asyncRefetch
                      }
                      isAsyncLoading={
                        options.find((option) => option.key === key)
                          ?.isAsyncLoading
                      }
                      onChange={(value) => {
                        setFilters({ ...filters, [key]: value })
                      }}
                    />
                  ) : getType(key) === "async-multi-select" ? (
                    <AsyncMultiSelect
                      value={isArray(value) ? value : value ? [value] : []}
                      isAsyncLoading={
                        options.find((option) => option.key === key)
                          ?.isAsyncLoading
                      }
                      maw={200}
                      options={getOptions(key)}
                      onChange={(value: string[]) => {
                        setFilters({ ...filters, [key]: value })
                      }}
                      asyncRefetch={
                        options.find((option) => option.key === key)
                          ?.asyncRefetch
                      }
                    />
                  ) : null}
                </Group>
                <ActionIcon
                  onClick={() => {
                    setFilters((_) => {
                      const newFilters = { ...filters }
                      delete newFilters[key]
                      return newFilters
                    })
                  }}
                  icon={<IconX />}
                />
              </Group>
            </Group>
          ))}
          {Boolean(filterLength) && <Divider />}
          <Stack gap="var(--mantine-spacing-xxs)">
            {filteredOptions.map((option, index) => (
              <Group
                key={`${option.key as string}-${index}`}
                justify="space-between"
              >
                <Text>{option.label}</Text>
                <ActionIcon
                  onClick={() => {
                    setFilters({
                      ...filters,
                      [option.key]: undefined,
                    })
                  }}
                  variant="tertiary"
                  icon={<IconCirclePlus />}
                />
              </Group>
            ))}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

export const getFilterButtonLabel = <T extends Partial<Record<string, any>>>(
  filters: {
    [key in keyof T]: T[key]
  },
  getLabel: (key: keyof T) => string,
  getOptionLabel: (key: keyof T, value: T[keyof T]) => string | null,
  title: string
): string => {
  if (Object.keys(filters).length === 1) {
    return `${getLabel(Object.keys(filters)[0])} :   ${getOptionLabel(Object.keys(filters)[0] as keyof T, filters[Object.keys(filters)[0] as keyof T]) ?? ""}`
  } else {
    return `${title} ${Object.keys(filters).length > 0 ? `(${Object.keys(filters).length})` : ""}`
  }
}
